import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Play, Zap, ChevronRight, ClipboardList } from 'lucide-react';
interface HeroProps {
  onOpenQuiz: () => void;
}
export function Hero({
  onOpenQuiz
}: HeroProps) {
  const questions = ['¿Con qué frecuencia tenés acidez?', '¿El reflujo interrumpe tu sueño?', '¿Cuánto tiempo llevás con esto?', '¿Tomás antiácidos regularmente?', '¿Sabés qué alimentos te afectan?', '¿Afecta tu calidad de vida?'];
  return <section id="hero" className="min-h-screen flex items-center bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(224,122,80,0.2)_0%,transparent_70%),linear-gradient(180deg,#120806_0%,#1a0f08_100%)] pt-[100px] pb-[80px] relative overflow-hidden" data-id="element-201">
      <div className="absolute inset-0 z-0" data-id="element-202">
        <img src="https://images.unsplash.com/photo-1543362906-acfc16c67564?w=800&q=60" alt="" className="w-full h-full object-cover opacity-[0.04]" loading="lazy" data-id="element-203" />
      </div>
      <div className="container-custom relative z-10" data-id="element-204">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[40px] md:gap-[60px] items-start" data-id="element-205">
          {/* LEFT COLUMN */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-206">
            <motion.span initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="inline-block bg-gradient-to-br from-gold/15 to-gold/5 border border-gold/30 text-gold text-[11px] tracking-[3px] uppercase px-5 py-2 rounded-[2px] mb-7" data-id="element-207">
              Mecanismo Cardias-Lock™ · Protocolo de Reprogramación Digestiva
            </motion.span>

            <h1 className="font-display text-[clamp(36px,5.5vw,68px)] font-black leading-[1.25] tracking-[-0.5px] mb-8 text-white uppercase" data-id="element-208">
              Adiós al Ardor, al Ácido que Sube y a las Noches Sin Dormir
              <span className="block text-[clamp(48px,8vw,96px)] mt-4 leading-[1.1] animate-shimmer" data-id="element-209">
                en 72 Horas
              </span>
            </h1>

            {/* Promise block */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.7
          }} className="mb-8 max-w-[520px]" data-id="element-210">
              <p className="text-[clamp(17px,2vw,21px)] text-white font-semibold leading-[1.6] mb-5" data-id="element-211">
                Si todo te da ardor, si no podés dormir porque se te sube el
                ácido, si el omeprazol ya no alcanza — este protocolo es para
                vos.{' '}
                <span className="relative inline-block" data-id="element-212">
                  <span className="relative z-10 text-gold font-black" data-id="element-213">
                    +1.200 personas ya dejaron atrás el reflujo.
                  </span>
                  <span className="absolute inset-x-0 bottom-0 h-[6px] bg-gold/20 rounded-sm -z-0" data-id="element-214" />
                </span>
              </p>

              <div className="border-l-[3px] border-gold pl-5 flex flex-col gap-3" data-id="element-215">
                {['Sin depender de omeprazol ni medicamentos de farmacia', 'Sin vinagre, bicarbonato ni remedios que no funcionan', 'Fortalecé la válvula que realmente falla'].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -12
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6 + i * 0.12
              }} className="flex items-center gap-2.5" data-id="element-216">
                    <span className="w-5 h-5 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0 text-gold text-[11px] font-black" data-id="element-217">
                      ✓
                    </span>
                    <span className="text-[15px] text-white/85 leading-snug" data-id="element-218">
                      {item}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            <div className="flex flex-col items-center sm:items-start gap-4" data-id="element-219">
              <a href="#oferta" className="text-[15px] font-semibold text-gold border-b border-gold/40 pb-0.5 hover:border-gold transition-colors tracking-[0.3px]" data-id="element-220">
                Ver el protocolo completo →
              </a>
            </div>
          </motion.div>

          {/* RIGHT COLUMN */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex flex-col gap-6" data-id="element-221">
            {/* Stats row */}
            <div className="flex gap-8 flex-wrap" data-id="element-222">
              {[{
              num: '72h',
              label: 'Para sentir cambios'
            }, {
              num: '6',
              label: 'Módulos del protocolo'
            }, {
              num: '3',
              label: 'Bonos incluidos'
            }].map((s, i) => <motion.div key={i} className="text-center" initial={{
              opacity: 0,
              y: 20,
              scale: 0.8
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              delay: 0.8 + i * 0.15,
              type: 'spring',
              bounce: 0.4
            }} data-id="element-223">
                  <span className="block font-display text-[32px] font-bold text-gold leading-none" data-id="element-224">
                    {s.num}
                  </span>
                  <span className="text-[11px] tracking-[1px] text-white/50 uppercase mt-1 block" data-id="element-225">
                    {s.label}
                  </span>
                </motion.div>)}
            </div>

            {/* Video placeholder */}
            <div className="relative rounded-[12px] overflow-hidden border border-gold/20 shadow-[0_30px_80px_rgba(0,0,0,0.6)] bg-[#201408] aspect-video flex items-center justify-center group cursor-pointer animate-border-glow" data-id="element-226">
              <div className="text-center text-gold/50" data-id="element-227">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center mx-auto mb-3 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(200,150,62,0.5)]" data-id="element-228">
                  <Play className="w-6 h-6 text-deep ml-1 fill-deep" data-id="element-229" />
                </div>
                <p className="text-[13px] tracking-[0.5px] text-white/60" data-id="element-230">
                  Video de presentación
                </p>
              </div>
            </div>

            {/* ===== DIAGNOSTIC CARD — below video ===== */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.6
          }} className="relative rounded-[14px] overflow-hidden cursor-pointer group" onClick={onOpenQuiz} data-id="element-231">
              {/* Animated pulsing border */}
              <div className="absolute inset-0 rounded-[14px] bg-gradient-to-br from-gold via-gold-light to-gold opacity-100 p-[1.5px]" data-id="element-232">
                <div className="w-full h-full rounded-[13px] bg-[#1a0f08]" data-id="element-233" />
              </div>
              {/* Glow pulse */}
              <motion.div animate={{
              opacity: [0.3, 0.7, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-[14px] shadow-[0_0_40px_rgba(224,122,80,0.4)] pointer-events-none" data-id="element-234" />

              <div className="relative z-10 p-6 bg-gradient-to-br from-gold/10 via-black/80 to-gold/5 rounded-[13px]" data-id="element-235">
                {/* Header row */}
                <div className="flex items-start justify-between mb-4" data-id="element-236">
                  <div className="flex items-center gap-3" data-id="element-237">
                    <motion.div animate={{
                    rotate: [0, 10, -10, 0]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} className="w-10 h-10 rounded-[10px] bg-gradient-to-br from-gold to-gold-light flex items-center justify-center shadow-[0_0_20px_rgba(200,150,62,0.5)]" data-id="element-238">
                      <ClipboardList className="w-5 h-5 text-deep" data-id="element-239" />
                    </motion.div>
                    <div data-id="element-240">
                      <p className="text-[10px] tracking-[2px] uppercase text-gold font-semibold" data-id="element-241">
                        Diagnóstico Gratuito
                      </p>
                      <p className="text-[13px] text-white font-semibold leading-tight" data-id="element-242">
                        Test de Riesgo Digestivo
                      </p>
                    </div>
                  </div>
                  <span className="bg-gradient-to-br from-gold to-gold-light text-deep text-[10px] font-black tracking-[1px] px-2.5 py-1 rounded-full" data-id="element-243">
                    GRATIS
                  </span>
                </div>

                {/* Question preview dots */}
                <p className="text-[12px] text-white/60 mb-3 font-medium" data-id="element-244">
                  6 preguntas · Resultado inmediato
                </p>
                <div className="flex flex-col gap-1.5 mb-5" data-id="element-245">
                  {questions.map((q, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.8 + i * 0.08
                }} className="flex items-center gap-2.5" data-id="element-246">
                      <div className="w-4 h-4 rounded-full border border-gold/40 flex items-center justify-center shrink-0" data-id="element-247">
                        <span className="text-[8px] text-gold font-bold" data-id="element-248">
                          {i + 1}
                        </span>
                      </div>
                      <span className="text-[12px] text-white/65 leading-tight" data-id="element-249">
                        {q}
                      </span>
                    </motion.div>)}
                </div>

                {/* CTA row */}
                <div className="flex items-center justify-between bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/30 rounded-[8px] px-4 py-3 group-hover:from-gold/30 group-hover:to-gold/20 transition-all duration-300" data-id="element-250">
                  <div className="flex items-center gap-2" data-id="element-251">
                    <Zap className="w-4 h-4 text-gold fill-gold" data-id="element-252" />
                    <span className="text-[13px] font-bold text-gold" data-id="element-253">
                      Descubrí tu nivel de riesgo
                    </span>
                  </div>
                  <motion.div animate={{
                  x: [0, 4, 0]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-254">
                    <ChevronRight className="w-5 h-5 text-gold" data-id="element-255" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}